// cSpell:ignore innen
// Maps between the dashboard `Invoice` shape and the rentio-api-next
// `PropertyCost` DTOs. This is the single place that resolves every
// front/back field-mapping gap, so components and engine.ts never change.

import {
  ICreatePropertyCost,
  IPropertyCost,
  IUpdatePropertyCost,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyCost";
import { ENoVatType } from "@rentiohq/shared-frontend/dist/types/contract.types";
import { toDay } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import {
  Category,
  Invoice,
  InvoiceScope,
  InvoiceTarget,
  VatValue,
} from "./types";

// vatPct (number | ENoVatType) -> the backend's split { vatPercentage, noVatType }.
const splitVat = (
  vatPct: VatValue,
): { vatPercentage?: number; noVatType?: ENoVatType } =>
  typeof vatPct === "number"
    ? { vatPercentage: vatPct }
    : { noVatType: vatPct };

// The backend's two VAT fields -> the UI's single vatPct.
const joinVat = (cost: IPropertyCost): VatValue =>
  cost.noVatType ?? cost.vatPercentage ?? 0;

// Resolve the numeric property id a cost belongs to. unitId (unit-level) and
// groupId (building-level) are both real, stringified property ids; the scene
// filter is the fallback. scope (common/private) distinguishes the two levels.
const resolvePropertyId = (
  inv: Pick<Invoice, "unitId" | "groupId">,
  filter: { groupId?: string; unitId?: string },
): number =>
  Number(inv.unitId ?? inv.groupId ?? filter.unitId ?? filter.groupId);

export const invoiceToCreateDto = (
  inv: Omit<Invoice, "id">,
  filter: { groupId?: string; unitId?: string },
): ICreatePropertyCost => {
  const { vatPercentage, noVatType } = splitVat(inv.vatPct);
  return {
    propertyId: resolvePropertyId(inv, filter),
    scope: inv.scope,
    date: inv.date,
    periodFrom: inv.periodFrom,
    periodTo: inv.periodTo,
    category: inv.category,
    categoryOther: inv.categoryOther,
    supplier: inv.supplier,
    description: inv.description,
    amountExclVat: inv.amountExclVat,
    vatPercentage,
    noVatType,
    target: inv.target,
    tenantSharePct: inv.tenantSharePct,
    paidAt: inv.paidAt,
    paymentRequestedAt: inv.paymentRequestedAt,
    paymentSplits: inv.paymentSplits,
    distribution: inv.distribution,
    // `documentUrl` carries the uploaded invoice document's id (see InvoiceModal).
    documentId: inv.documentUrl,
    groupCostRef: inv.groupCostRef,
  };
};

export const invoicePatchToUpdateDto = (
  patch: Partial<Invoice>,
): IUpdatePropertyCost => {
  const dto: IUpdatePropertyCost = {};

  if ("scope" in patch) dto.scope = patch.scope;
  if ("date" in patch) dto.date = patch.date;
  if ("periodFrom" in patch) dto.periodFrom = patch.periodFrom;
  if ("periodTo" in patch) dto.periodTo = patch.periodTo;
  if ("category" in patch) dto.category = patch.category;
  if ("categoryOther" in patch) dto.categoryOther = patch.categoryOther ?? null;
  if ("supplier" in patch) dto.supplier = patch.supplier;
  if ("description" in patch) dto.description = patch.description ?? null;
  if ("amountExclVat" in patch) dto.amountExclVat = patch.amountExclVat;
  if ("vatPct" in patch && patch.vatPct !== undefined) {
    const { vatPercentage, noVatType } = splitVat(patch.vatPct);
    // Setting one VAT field clears the other.
    dto.vatPercentage = vatPercentage ?? null;
    dto.noVatType = noVatType ?? null;
  }
  if ("target" in patch) dto.target = patch.target;
  if ("tenantSharePct" in patch)
    dto.tenantSharePct = patch.tenantSharePct ?? null;
  // undefined -> null so "mark unpaid" / "un-request" actually clears the column.
  if ("paidAt" in patch) dto.paidAt = patch.paidAt ?? null;
  if ("paymentRequestedAt" in patch)
    dto.paymentRequestedAt = patch.paymentRequestedAt ?? null;
  if ("paymentSplits" in patch) dto.paymentSplits = patch.paymentSplits ?? null;
  if ("distribution" in patch) dto.distribution = patch.distribution ?? null;
  if ("documentUrl" in patch) dto.documentId = patch.documentUrl ?? null;
  if ("groupCostRef" in patch) dto.groupCostRef = patch.groupCostRef ?? null;

  return dto;
};

export const propertyCostToInvoice = (cost: IPropertyCost): Invoice => ({
  id: cost.id,
  // A cost is tied to one property; expose it as unitId so existing label
  // lookups (which search the real assignable properties) resolve for both
  // unit- and building-level costs.
  unitId: String(cost.propertyId),
  groupId: undefined,
  scope: cost.scope as InvoiceScope,
  date: toDay(cost.date) ?? "",
  periodFrom: toDay(cost.periodFrom) ?? "",
  periodTo: toDay(cost.periodTo) ?? "",
  category: cost.category as Category,
  categoryOther: cost.categoryOther ?? undefined,
  supplier: cost.supplier,
  description: cost.description ?? undefined,
  amountExclVat: cost.amountExclVat,
  vatPct: joinVat(cost),
  target: cost.target as InvoiceTarget,
  tenantSharePct: cost.tenantSharePct ?? undefined,
  paidAt: toDay(cost.paidAt),
  paymentRequestedAt: toDay(cost.paymentRequestedAt),
  // Sanitize legacy splits: early versions of the wizard wrote a
  // `requestedAt` on every split even when no paymentOrder actually got
  // created for that tenant. Strip that here so the UI can honestly show
  // "Nog te innen" for unlinked splits.
  paymentSplits:
    cost.paymentSplits?.map(s =>
      s.paymentOrderId ? s : { ...s, requestedAt: undefined },
    ) ?? undefined,
  distribution: cost.distribution ?? undefined,
  documentUrl: cost.documentId ?? undefined,
  groupCostRef: cost.groupCostRef ?? undefined,
  viewerPart: cost.viewerPart || undefined,
});
